/**
 * Formateo del tracker.
 *
 * Regla transversal: **nada de aquí puede depender de la zona horaria del
 * entorno**. El servidor pinta el HTML y el cliente lo rehidrata; si una hora
 * se formateara con `TZ` del proceso (UTC en producción) y otra con la del
 * navegador, React tiraría el árbol entero. Por eso todas las horas se
 * calculan desplazando el instante y formateándolo **como UTC**: el resultado
 * solo depende del desfase, que sí viaja como dato.
 */

import type { TrackerLocale } from '@/lib/santa-tracker'

const HOUR_MS = 3_600_000

const numberFormatters = new Map<string, Intl.NumberFormat>()

function numberFormatter(locale: TrackerLocale): Intl.NumberFormat {
  const cached = numberFormatters.get(locale)
  if (cached) return cached
  const formatter = new Intl.NumberFormat(locale)
  numberFormatters.set(locale, formatter)
  return formatter
}

/** Entero con separador de miles del idioma. */
export function formatNumber(value: number, locale: TrackerLocale): string {
  return numberFormatter(locale).format(Math.round(value))
}

/**
 * Cifras muy grandes en versión corta ("2,1 mil M") para que quepan en las
 * tarjetas de móvil sin partirse en dos líneas.
 */
export function formatCompact(value: number, locale: TrackerLocale): string {
  if (value < 100_000) return formatNumber(value, locale)
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

const timeFormatters = new Map<string, Intl.DateTimeFormat>()

function timeFormatter(locale: TrackerLocale): Intl.DateTimeFormat {
  const cached = timeFormatters.get(locale)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale === 'en',
    timeZone: 'UTC',
  })
  timeFormatters.set(locale, formatter)
  return formatter
}

/** Hora del reloj en el desfase indicado. Determinista: no mira el `TZ` local. */
export function formatLocalTime(
  instantMs: number,
  utcOffsetHours: number,
  locale: TrackerLocale
): string {
  return timeFormatter(locale).format(new Date(instantMs + utcOffsetHours * HOUR_MS))
}

/** Duración en horas y minutos ("31 h 12 min"). */
export function formatDuration(minutes: number, locale: TrackerLocale): string {
  const hours = Math.floor(minutes / 60)
  const rest = Math.round(minutes % 60)
  const unit = locale === 'en' ? 'min' : 'min'
  return hours > 0 ? `${hours} h ${rest} ${unit}` : `${rest} ${unit}`
}

/** Cuenta atrás compacta para textos en línea ("2 d 04:31:09"). */
export function formatCountdownInline(parts: {
  days: number
  hours: number
  minutes: number
  seconds: number
}): string {
  const clock = [parts.hours, parts.minutes, parts.seconds]
    .map((value) => String(value).padStart(2, '0'))
    .join(':')
  return parts.days > 0 ? `${parts.days} d ${clock}` : clock
}
